import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AIRequestConfig, ChatMessage, sendChatMessage, storeChatHistory, fetchThreadMessages } from "@/utils/api";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQueryLimits } from "@/hooks/use-query-limits";
import { Session } from "@supabase/supabase-js";

const getInitialMessage = (isMobile: boolean): ChatMessage => ({
  role: "assistant" as const,
  content: "" // Removed greeting to match UI improvements
});

// Message shown when user has reached their query limit
const getLimitReachedMessage = (): ChatMessage => ({
  role: "assistant" as const,
  content: "You've reached your daily query limit of 5 questions. Subscribe to our premium plan for unlimited queries and full access to all features."
});

export function useChat() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>([getInitialMessage(isMobile)]);
  const [isLoading, setIsLoading] = useState(false);
  const [canAskQuestion, setCanAskQuestion] = useState(true);
  const [enableBellwether, setEnableBellwether] = useState(true);
  const [enableMacro, setEnableMacro] = useState(true);
  
  // Thread management state
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  
  const { toast } = useToast();
  
  // Get user session and query limits
  const [session, setSession] = useState<Session | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [lastUserMessage, setLastUserMessage] = useState<string | null>(null);
  
  // Get query limits information from the hook
  const queryLimitsResult = useQueryLimits(session);
  
  /**
   * DEVELOPER NOTE:
   * 
   * Query limits can be disabled for development/testing purposes.
   * 
   * To control query limits behavior:
   * 1. In development: Set VITE_DISABLE_QUERY_LIMITS=true in your .env file
   * 2. In production: Remove this env var or set to false
   * 
   * When disabled, all users will be treated as subscribed premium users
   * with unlimited queries, regardless of their actual subscription status.
   */
  const disableQueryLimits = import.meta.env.VITE_DISABLE_QUERY_LIMITS === 'true';
  
  // In development, we may want to disable query limits entirely
  // This flag controls whether we apply query limits or not
  const applyQueryLimits = !disableQueryLimits;
  
  // Create modified limits object that respects the development mode setting
  const {
    hasReachedLimit,
    isSubscribed,
    isTrialing,
    remainingQueries,
    incrementQueryCount,
    checkQueryLimits
  } = {
    ...queryLimitsResult,
    // Override limits when in development mode
    hasReachedLimit: applyQueryLimits ? queryLimitsResult.hasReachedLimit : false,
    isSubscribed: applyQueryLimits ? queryLimitsResult.isSubscribed : true,
    isTrialing: applyQueryLimits ? queryLimitsResult.isTrialing : false,
  };

  // Set initial query limit state
  useEffect(() => {
    setCanAskQuestion(!hasReachedLimit);
  }, [hasReachedLimit]);

  // Get session
  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setCurrentUserId(data.session?.user?.id || null);
    };
    
    getSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setCurrentUserId(session?.user?.id || null);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Handle completed chat streaming
  useEffect(() => {
    const handleStreamingComplete = async (event: CustomEvent<{
      completeContent: string;
    }>) => {
      if (event.detail.completeContent && lastUserMessage && currentUserId) {
        console.log("[useChat] Stream complete, storing chat history");
        
        // Store with current thread ID, or create new thread if none exists
        const result = await storeChatHistory(
          currentUserId,
          lastUserMessage,
          event.detail.completeContent,
          currentThreadId || undefined
        );
        
        // If we didn't have a thread ID, use the returned ID as our new thread
        if (!currentThreadId && result.id) {
          setCurrentThreadId(result.id);
          
          // Update URL with the new conversation ID
          const url = new URL(window.location.href);
          url.searchParams.set('conversationId', result.id);
          window.history.pushState({}, '', url.toString());
        }
      }
    };

    window.addEventListener('assistantResponseComplete', handleStreamingComplete as EventListener);
    
    return () => {
      window.removeEventListener('assistantResponseComplete', handleStreamingComplete as EventListener);
    };
  }, [lastUserMessage, currentUserId, currentThreadId]);

  // Handle streaming updates
  useEffect(() => {
    const handleStreamingUpdate = (event: CustomEvent<{
      content?: string;
      final?: boolean;
    }>) => {
      if (event.detail.content) {
        // Turn off loading indicator as soon as first content arrives
        setIsLoading(false);
        
        // Update the last message in the array (which should be the assistant's message)
        setMessages(prevMessages => {
          // Only update if the last message is from the assistant
          if (prevMessages.length > 0 && prevMessages[prevMessages.length - 1].role === 'assistant') {
            const newMessages = [...prevMessages];
            const lastMessage = newMessages[newMessages.length - 1];
            
            // Update the content of the last message
            newMessages[newMessages.length - 1] = {
              ...lastMessage,
              content: (lastMessage.content as string) + event.detail.content
            };
            
            return newMessages;
          }
          return prevMessages;
        });
      }
    };

    window.addEventListener('assistantResponse', handleStreamingUpdate as EventListener);
    
    return () => {
      window.removeEventListener('assistantResponse', handleStreamingUpdate as EventListener);
    };
  }, []);

  // Reset messages when mobile state changes
  useEffect(() => {
    setMessages([getInitialMessage(isMobile)]);
  }, [isMobile]);

  // Reset chat to initial state (for new initialQuestion)
  const resetChat = () => {
    setMessages([getInitialMessage(isMobile)]);
    setIsLoading(false);
    setCanAskQuestion(!hasReachedLimit);
    setEnableBellwether(true);
    setEnableMacro(true);
    setLastUserMessage(null);
  };

  // Start a new chat thread
  const startNewChat = () => {
    setCurrentThreadId(null);
    setMessages([getInitialMessage(isMobile)]);
    setIsLoading(false);
    setCanAskQuestion(!hasReachedLimit);
    setLastUserMessage(null);
    setEnableBellwether(true);
    setEnableMacro(true);
    
    // Clear conversationId from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('conversationId');
    window.history.pushState({}, '', url.toString());
  };

  // Load an existing thread
  const loadThread = useCallback(async (threadId: string) => {
    if (!currentUserId) return;
    
    try {
      setIsLoading(true);
      const { messages: threadMessages, error } = await fetchThreadMessages(threadId, currentUserId);
      
      if (error) {
        console.error('[useChat] Error loading thread:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load conversation. Please try again.",
        });
        return;
      }
      
      // Convert database messages to ChatMessage format
      const chatMessages: ChatMessage[] = [getInitialMessage(isMobile)];
      
      threadMessages.forEach(msg => {
        chatMessages.push(
          { role: "user", content: msg.question },
          { role: "assistant", content: msg.response }
        );
      });
      
      setCurrentThreadId(threadId);
      setMessages(chatMessages);
      
      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('conversationId', threadId);
      window.history.pushState({}, '', url.toString());
      
    } catch (error) {
      console.error('[useChat] Error loading thread:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load conversation. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, isMobile, toast]);

  // Handle URL-based thread loading
  useEffect(() => {
    if (!currentUserId) return;
    
    const url = new URL(window.location.href);
    const conversationId = url.searchParams.get('conversationId');
    
    if (conversationId && conversationId !== currentThreadId) {
      console.log('[useChat] Loading thread from URL:', conversationId);
      loadThread(conversationId);
    }
  }, [currentUserId, currentThreadId, loadThread, location.search]);

  const sendMessage = async (userMessage: string) => {
    if (!userMessage.trim() || isLoading) return;

    try {
      setIsLoading(true);
      setCanAskQuestion(false);
      setLastUserMessage(userMessage);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('User must be logged in to use the chat');
      }

      // Check query limits only if we're enforcing them
      if (applyQueryLimits && !isSubscribed && !isTrialing) {
        // Refresh limits status
        await checkQueryLimits();
        
        if (hasReachedLimit) {
          setMessages([
            getInitialMessage(isMobile),
            {
              role: "user",
              content: userMessage
            },
            getLimitReachedMessage()
          ]);
          return;
        }
      }

      console.log("[useChat] Starting new chat interaction with message:", userMessage);
      console.log("[useChat] Current thread ID:", currentThreadId);
      console.log("[useChat] Current state - Bellwether:", enableBellwether, "Macro:", enableMacro);
      
      const apiConfig: AIRequestConfig = {};

      if (enableBellwether) {
        apiConfig.enable_bellwether = true;
      }

      if (enableMacro) {
        apiConfig.enable_macro = true;
      }
      
      // Add the new user message to existing conversation
      const newUserMessage: ChatMessage = {
        role: "user",
        content: userMessage
      };
      
      const newAssistantMessage: ChatMessage = {
        role: "assistant",
        content: "" // Empty content that will be filled by streaming
      };
      
      // If we're in a thread, preserve existing messages; otherwise start fresh
      setMessages(prevMessages => {
        if (currentThreadId && prevMessages.length > 1) {
          // We're in an existing thread - append to existing conversation
          return [...prevMessages, newUserMessage, newAssistantMessage];
        } else {
          // New thread - start with initial message
          return [getInitialMessage(isMobile), newUserMessage, newAssistantMessage];
        }
      });
      
      // Send message via API - streaming will be handled automatically
      await sendChatMessage(userMessage, session.user.id, apiConfig, messages);
      
      // Only increment the query count for free users and if enforcing limits
      if (applyQueryLimits && !isSubscribed && !isTrialing) {
        const canContinue = await incrementQueryCount();
        
        // Show remaining queries toast if user is approaching their limit
        if (remainingQueries > 0 && remainingQueries <= 2) {
          toast({
            title: remainingQueries === 1 ? "Last query remaining" : "Limited queries remaining",
            description: `You have ${remainingQueries} ${remainingQueries === 1 ? 'query' : 'queries'} remaining today.`,
          });
        }
        
        // If user has reached their limit, update canAskQuestion state
        if (!canContinue) {
          setCanAskQuestion(false);
        }
      }

    } catch (error) {
      console.error("[useChat] Error in sendMessage:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get response from AI. Please try again.",
      });
      // Reset to initial state on error
      setMessages([getInitialMessage(isMobile)]);
    } finally {
      setIsLoading(false);
      // When query limits are disabled, always allow more questions
      setCanAskQuestion(!applyQueryLimits ? true : !hasReachedLimit);
    }
  };

  const handleBellwetherToggle = (value: boolean) => {
    console.log("[useChat] Setting Bellwether to:", value);
    setEnableBellwether(value);
  };

  const handleMacroToggle = (value: boolean) => {
    console.log("[useChat] Setting Macro to:", value);
    setEnableMacro(value);
  };

  // Memoize setMessages to prevent infinite re-renders in components that depend on it
  const memoizedSetMessages = useCallback((messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    setMessages(messages);
  }, []);

  return {
    messages,
    setMessages: memoizedSetMessages, // Use memoized version
    isLoading,
    canAskQuestion,
    enableBellwether,
    setEnableBellwether,
    enableMacro,
    setEnableMacro,
    resetChat,
    startNewChat,
    loadThread,
    currentThreadId,
    sendMessage,
    lastUserMessage,
    hasReachedLimit,
    isSubscribed: queryLimitsResult.isSubscribed,
    isTrialing: queryLimitsResult.isTrialing,
    remainingQueries: queryLimitsResult.remainingQueries,
  };
}
